import { Num, T } from 'gt-react';

import {
  AlertContainer,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import type { Conversion } from './conversion';
import { InvalidResults } from './InvalidResults';
import { IssuesSummary } from './IssuesSummary';
import { SkippedRowsViewer } from './SkippedRowsViewer';

interface Props {
  fileName: string | null;
  /** The dropped workbook's headers, for showing skipped rows back. */
  headers: string[];
  readError: string | null;
  conversion: Conversion;
}

/** What happened to the dropped file: unreadable, refused, or converted. */
export function ConversionReport({
  fileName,
  headers,
  readError,
  conversion,
}: Props) {
  if (readError) {
    return (
      <AlertContainer variant="destructive">
        <AlertTitle>
          <T context="error title when the Results workbook cannot be read">
            Could not read the workbook
          </T>
        </AlertTitle>
        <AlertDescription>{readError}</AlertDescription>
      </AlertContainer>
    );
  }
  if (conversion.result?.status === 'invalid') {
    return <InvalidResults result={conversion.result} />;
  }
  if (!conversion.checked) return null;
  const { upload, summary } = conversion.checked;
  return (
    <Card className="flex flex-col gap-3 p-3">
      <div className="text-sm">
        <span className="font-medium">{fileName}</span>
        {' — '}
        <T context="Results to Upload conversion summary">
          <Num>{upload.rowsRead}</Num> rows read,{' '}
          <Num>{upload.rowsWritten}</Num> written,{' '}
          <Num>{upload.skipped.length}</Num> skipped.
        </T>
      </div>
      <IssuesSummary summary={summary} />
      <SkippedRowsViewer headers={headers} skipped={upload.skipped} />
    </Card>
  );
}
